import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    retrieveUsersApi,
} from "../../../components/api/UserApiService"; // user API dosyanı buraya göre import et
import {
    retrieveBirthdayPersonsApi,
    createBirthdayPersonApi,
    deleteBirthdayPersonApi,
    updateBirthdayPersonApi,
} from "../../../components/api/BirthdayPersonApiService";

const BirthdayView = () => {
    const [users, setUsers] = useState([]);
    const [birthdayPersons, setBirthdayPersons] = useState([]);

    const [nameFilter, setNameFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editingPerson, setEditingPerson] = useState(null);
    const [newPerson, setNewPerson] = useState({
        firstName: "",
        lastName: "",
        birthDate: "", // "YYYY-MM-DD"
    });

    useEffect(() => {
        loadUsers();
        loadBirthdayPersons();
    }, []);

    const loadUsers = () => {
        retrieveUsersApi()
            .then((res) => {
                setUsers(res?.data || []);
            })
            .catch((err) => {
                console.log(err?.response?.data);
            });
    };

    const loadBirthdayPersons = () => {
        retrieveBirthdayPersonsApi()
            .then((res) => {
                setBirthdayPersons(res?.data || []);
            })
            .catch((err) => {
                console.log(err?.response?.data);
            });
    };

    // Filtrelenmiş kullanıcılar (sadece ACTIVE ve doğum günü dolu olanlar)
    const filteredUsers = users
        ?.filter((u) => u.birthDate && (u.recordStatus === "ACTIVE" || !u.recordStatus))
        ?.filter((u) => {
            if (!nameFilter) return true;
            const displayName = u.name || u.username || "";
            return displayName.toLowerCase().includes(nameFilter.toLowerCase());
        })
        ?.filter((u) => {
            if (!monthFilter) return true;
            try {
                const d = new Date(u.birthDate);
                const m = d.getMonth() + 1; // 0-11
                return String(m) === String(monthFilter);
            } catch {
                return true;
            }
        });

    // Filtrelenmiş BirthdayPerson kayıtları (sadece ACTIVE geliyor zaten)
    const filteredBirthdayPersons = birthdayPersons
        ?.filter((p) => {
            if (!nameFilter) return true;
            const fullName = `${p.firstName || ""} ${p.lastName || ""}`.trim();
            return fullName.toLowerCase().includes(nameFilter.toLowerCase());
        })
        ?.filter((p) => {
            if (!monthFilter) return true;
            try {
                const d = new Date(p.birthDate);
                const m = d.getMonth() + 1;
                return String(m) === String(monthFilter);
            } catch {
                return true;
            }
        });

    const handleOpenAdd = () => {
        setEditingPerson(null);
        setNewPerson({
            firstName: "",
            lastName: "",
            birthDate: "",
        });
        setAddDialogOpen(true);
    };

    const handleOpenEdit = (p) => {
        setEditingPerson(p);
        setNewPerson({
            firstName: p.firstName || "",
            lastName: p.lastName || "",
            birthDate: p.birthDate || "",
        });
        setAddDialogOpen(true);
    };

    const handleSaveNewPerson = () => {
        if (!newPerson.firstName || !newPerson.birthDate) {
            toast.error("First name and birth date are required.");
            return;
        }

        if (editingPerson) {
            updateBirthdayPersonApi(editingPerson.id, newPerson)
                .then(() => {
                    toast.success("Birthday person updated.");
                    setAddDialogOpen(false);
                    setEditingPerson(null);
                    loadBirthdayPersons();
                })
                .catch((err) => {
                    console.log(err?.response?.data);
                    toast.error("Something went wrong.");
                });
        } else {
            createBirthdayPersonApi(newPerson)
                .then(() => {
                    toast.success("Birthday person added.");
                    setAddDialogOpen(false);
                    loadBirthdayPersons();
                })
                .catch((err) => {
                    console.log(err?.response?.data);
                    toast.error("Something went wrong.");
                });
        }
    };

    const handleDeletePerson = (id) => {
        if (!window.confirm("Are you sure you want to delete this birthday reminder?")) {
            return;
        }
        deleteBirthdayPersonApi(id)
            .then(() => {
                toast.success("Birthday person deleted (soft delete).");
                loadBirthdayPersons();
            })
            .catch((err) => {
                console.log(err?.response?.data);
                toast.error("Something went wrong.");
            });
    };

    const formatBirthDate = (value) => {
        if (!value) return "";
        try {
            return format(new Date(value), "dd-MMM");
        } catch {
            return value;
        }
    };

    const isBirthdayToday = (dateString) => {
        if (!dateString) return false;

        const today = new Date();
        const d = new Date(dateString);

        return (
            d.getDate() === today.getDate() &&
            d.getMonth() === today.getMonth()
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: "#1E2685" }}>
                Birthdays
            </Typography>

            {/* Filtreler */}
            <Box
                sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid #ddd",
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Name contains"
                            size="small"
                            fullWidth
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            label="Month (1-12)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 12 }}
                            value={monthFilter}
                            onChange={(e) => setMonthFilter(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                setNameFilter("");
                                setMonthFilter("");
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleOpenAdd}
                        >
                            Add Birthday Person
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Sistem kullanıcılarının doğum günleri */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    System Users
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">Name</TableCell>
                                <TableCell align="center">Birth Date</TableCell>
                                <TableCell align="center">Record Status</TableCell>
                                <TableCell align="center">Delete</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers?.map((u) => (
                                <TableRow key={u.id}
                                    sx={{
                                        backgroundColor: isBirthdayToday(u.birthDate)
                                            ? "#9ce6a6"
                                            : "inherit",
                                    }}>
                                    <TableCell align="center">{u.name}</TableCell>
                                    <TableCell align="center">{formatBirthDate(u.birthDate)}</TableCell>
                                    <TableCell align="center">{u.recordStatus}</TableCell>
                                    <TableCell align="center">
                                        {/* User doğum günleri silinemez */}
                                        <IconButton size="small" disabled>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredUsers?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No users with birthday found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Sisteme dışarıdan eklenen kişiler */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Other Birthday Persons
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">ID</TableCell>
                                <TableCell align="center">Name</TableCell>
                                <TableCell align="center">Birth Date</TableCell>
                                {/*<TableCell align="center">Created By</TableCell>*/}
                                <TableCell align="center">Record Status</TableCell>
                                <TableCell align="center">Delete</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredBirthdayPersons?.map((p) => (
                                <TableRow key={p.id}
                                    sx={{
                                        backgroundColor: isBirthdayToday(p.birthDate)
                                            ? "#9ce6a6"
                                            : "inherit",
                                    }}>
                                    <TableCell align="center">{p.id}</TableCell>
                                    <TableCell align="center">{p.lastName ? (p.firstName + " " + p.lastName) : p.firstName}</TableCell>
                                    <TableCell align="center">{formatBirthDate(p.birthDate)}</TableCell>
                                    {/*<TableCell align="center">
                    {p.createdBy ? p.createdBy.username || p.createdBy.email : ""}
                  </TableCell>*/}
                                    <TableCell align="center">{p.recordStatus}</TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleOpenEdit(p)}
                                            >
                                                Edit
                                            </Button>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeletePerson(p.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredBirthdayPersons?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No birthday persons found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Yeni BirthdayPerson ekleme dialogu */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingPerson ? "Edit Birthday Person" : "Add Birthday Person"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="First Name"
                                    size="small"
                                    fullWidth
                                    value={newPerson.firstName}
                                    onChange={(e) =>
                                        setNewPerson({ ...newPerson, firstName: e.target.value })
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Last Name"
                                    size="small"
                                    fullWidth
                                    value={newPerson.lastName}
                                    onChange={(e) =>
                                        setNewPerson({ ...newPerson, lastName: e.target.value })
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Birth Date"
                                    size="small"
                                    fullWidth
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={newPerson.birthDate}
                                    onChange={(e) =>
                                        setNewPerson({ ...newPerson, birthDate: e.target.value })
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveNewPerson}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BirthdayView;
